/**
 * @typedef {{
 *   name?: string;
 *   email?: string;
 *   phone?: string;
 *   address?: string;
 *   addressLine2?: string;
 *   city?: string;
 *   state?: string;
 *   province?: string;
 *   postalCode?: string;
 *   country?: string;
 * }} CheckoutBillingDetails
 */

/**
 * @typedef {{
 *   line1: string;
 *   line2: string;
 *   city: string;
 *   state: string;
 *   postal_code: string;
 *   country: string;
 * }} StripeBillingAddress
 */

/**
 * @typedef {{
 *   name?: string;
 *   email?: string;
 *   phone?: string;
 *   address?: StripeBillingAddress;
 * }} StripeBillingDetails
 */

/**
 * Payment Element with address: "never" requires the full address object,
 * including state, even when the country does not use provinces.
 * @param {CheckoutBillingDetails | null | undefined} billingDetails
 * @returns {StripeBillingAddress}
 */
export function buildStripeBillingAddress(billingDetails) {
  const source = billingDetails && typeof billingDetails === "object" ? billingDetails : {};
  const country = String(source.country || "").trim().toUpperCase();
  return {
    line1: String(source.address || "").trim(),
    line2: String(source.addressLine2 || "").trim(),
    city: String(source.city || "").trim(),
    state: String(source.state || source.province || "").trim(),
    postal_code: String(source.postalCode || "").trim(),
    country: /^[A-Z]{2}$/.test(country) ? country : "",
  };
}

function addressHasValues(address) {
  return Boolean(
    address.line1 ||
    address.line2 ||
    address.city ||
    address.state ||
    address.postal_code ||
    address.country,
  );
}

/**
 * Maps checkout billing (collected once in step 1) to Stripe billing_details.
 * @param {CheckoutBillingDetails | null | undefined} billingDetails
 * @returns {StripeBillingDetails}
 */
export function mapBillingDetailsToStripe(billingDetails) {
  if (!billingDetails || typeof billingDetails !== "object") {
    return {};
  }

  const name = String(billingDetails.name || "").trim();
  const email = String(billingDetails.email || "").trim();
  const phone = String(billingDetails.phone || "").trim();
  const address = buildStripeBillingAddress(billingDetails);

  /** @type {StripeBillingDetails} */
  const details = {};
  if (name) {
    details.name = name;
  }
  if (email) {
    details.email = email;
  }
  if (phone) {
    details.phone = phone;
  }
  if (addressHasValues(address)) {
    details.address = address;
  }

  return details;
}

/**
 * Address-only payload for Payment Element confirm (name/email stay on the element).
 * Always includes state so Stripe confirmPayment accepts address: "never".
 * @param {CheckoutBillingDetails | null | undefined} billingDetails
 * @returns {{ address: StripeBillingAddress }}
 */
export function mapBillingAddressToStripe(billingDetails) {
  return { address: buildStripeBillingAddress(billingDetails) };
}
